import { useState } from 'react'
import { Logo } from '../branding/Logo'
import { FloatingGames } from './FloatingGames'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

type Mode = 'landing' | 'signin' | 'signup' | 'forgot'

export function AuthScreen() {
  const t = useT()
  const {
    signInWithApple, signInWithGoogle, signInWithIdentifier, signUpWithUsername,
    sendPasswordReset, enterAsGuest, configured,
  } = useAuth()
  const [mode, setMode] = useState<Mode>('landing')
  const [identifier, setIdentifier] = useState('')   // email or username
  const [email, setEmail]           = useState('')
  const [username, setUsername]     = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword]     = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true); setError(null)
    try {
      await signInWithIdentifier(identifier, password)
    } catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true); setError(null)
    try {
      await signUpWithUsername({
        username,
        email,
        password,
        displayName: displayName || username,
      })
    } catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true); setError(null); setResetSent(false)
    try {
      await sendPasswordReset(email)
      setResetSent(true)
    } catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  async function handleApple() {
    setBusy(true); setError(null)
    try { await signInWithApple() }
    catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  async function handleGoogle() {
    setBusy(true); setError(null)
    try { await signInWithGoogle() }
    catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  async function handleGuest() {
    setBusy(true); setError(null)
    try { await enterAsGuest() }
    catch (err) { setError(readError(err)) }
    finally { setBusy(false) }
  }

  const setupBanner = !configured && (
    <div className="mx-5 mb-4 rounded-2xl border border-brand-border bg-white/80 backdrop-blur-xs px-4 py-3 text-center">
      <p className="text-sm font-semibold text-brand-text">{t("Supabase isn't configured yet")}</p>
      <p className="text-xs text-brand-muted mt-0.5">
        {t('See')} <span className="font-mono">{t('SUPABASE_SETUP.md')}</span> {t('to finish setup.')}
      </p>
    </div>
  )

  // ─── Sign in ───────────────────────────────────────────────────────────
  if (mode === 'signin') {
    const ready = identifier.trim().length > 0 && password.length >= 6
    return (
      <FormShell onBack={() => { setMode('landing'); setError(null) }}>
        <FormHeader title={t('Sign in')} subtitle={t('Use your username or email.')} />
        <form onSubmit={handleSignIn} className="space-y-4">
          <Field label={t('Username or email')}>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder={t('jodan or you@email.com')}
              required
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              className={inputClass}
            />
          </Field>
          <Field label={t('Password')}>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('Your password')}
              required
              minLength={6}
              className={inputClass}
            />
          </Field>

          {error && <p className="text-sm text-brand-danger">{error}</p>}

          <button type="submit" className="btn-primary" disabled={busy || !ready}>
            {busy ? t('Signing in…') : t('Sign in')}
          </button>
        </form>

        <div className="text-center text-sm">
          <button
            type="button"
            onClick={() => { setMode('forgot'); setError(null); setResetSent(false) }}
            className="text-brand-muted underline underline-offset-2"
          >
            {t('Forgot password?')}
          </button>
        </div>
        <p className="text-center text-sm text-brand-muted">
          New here?{' '}
          <button
            type="button"
            onClick={() => { setMode('signup'); setError(null) }}
            className="text-brand-text font-semibold underline underline-offset-2"
          >
            {t('Create an account')}
          </button>
        </p>
      </FormShell>
    )
  }

  // ─── Sign up ───────────────────────────────────────────────────────────
  if (mode === 'signup') {
    const usernameOk = /^[a-z][a-z0-9_]{2,23}$/.test(username.trim().toLowerCase())
    const ready = usernameOk && email.includes('@') && password.length >= 6
    return (
      <FormShell onBack={() => { setMode('landing'); setError(null) }}>
        <FormHeader
          title={t('Create account')}
          subtitle={t("Pick a username. That's how you'll sign in.")}
        />
        <form onSubmit={handleSignUp} className="space-y-4">
          <Field label={t('Username')}>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 24))}
              placeholder={t('jodan')}
              required
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              className={inputClass}
            />
            <p className="text-[11px] text-brand-muted mt-1">
              {t('3–24 chars, lowercase letters / digits / underscore. Starts with a letter.')}
            </p>
          </Field>
          <Field label={t('Display name (optional)')}>
            <input
              type="text"
              name="nickname"
              autoComplete="nickname"
              value={displayName}
              onChange={e => setDisplayName(e.target.value.slice(0, 24))}
              placeholder={t('Free-form, shown at game seats')}
              className={inputClass}
            />
          </Field>
          <Field label={t('Email')}>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('your@email.com')}
              required
              autoCapitalize="none"
              autoCorrect="off"
              className={inputClass}
            />
          </Field>
          <Field label={t('Password')}>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('At least 6 characters')}
              required
              minLength={6}
              className={inputClass}
            />
          </Field>

          {error && <p className="text-sm text-brand-danger">{error}</p>}

          <button type="submit" className="btn-primary" disabled={busy || !ready}>
            {busy ? t('Creating account…') : t('Create account')}
          </button>
        </form>

        <p className="text-center text-sm text-brand-muted">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => { setMode('signin'); setError(null) }}
            className="text-brand-text font-semibold underline underline-offset-2"
          >
            {t('Sign in')}
          </button>
        </p>
      </FormShell>
    )
  }

  // ─── Forgot password ───────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <FormShell onBack={() => { setMode('signin'); setError(null); setResetSent(false) }}>
        <FormHeader
          title={t('Reset password')}
          subtitle={t("We'll email you a link to set a new password.")}
        />
        <form onSubmit={handleForgot} className="space-y-4">
          <Field label={t('Email')}>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('your@email.com')}
              required
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              className={inputClass}
            />
          </Field>

          {error && <p className="text-sm text-brand-danger">{error}</p>}

          <button type="submit" className="btn-primary" disabled={busy || !email.includes('@')}>
            {busy ? 'Sending…' : t('Send reset link')}
          </button>
        </form>

        {resetSent && (
          <p className="text-sm text-iet-green text-center">{t('Sent. Check your inbox (and spam folder).')}</p>
        )}
      </FormShell>
    )
  }

  // ─── Landing ───────────────────────────────────────────────────────────
  return (
    <div className="screen relative overflow-hidden">
      <FloatingGames />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-12 pb-8">
        <Logo
          size={220}
          className="mb-8 w-[60vw] max-w-[260px] h-auto
                     drop-shadow-[0_8px_24px_rgba(200,219,74,0.35)]"
        />
        <p className="text-brand-text/80 font-medium text-balance text-center max-w-xs">
          {t('Creative games to play on the cloud')}
        </p>
      </div>

      <div
        className="relative z-10 px-6 pb-10 pt-10 space-y-3"
        style={{
          background:
            'linear-gradient(to top, #FAFAF8 0%, #FAFAF8 70%, rgba(250,250,248,0) 100%)',
        }}
      >
        {setupBanner}

        <button
          onClick={() => { setMode('signin'); setError(null) }}
          disabled={busy}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {t('Sign in')}
        </button>

        <button
          onClick={() => { setMode('signup'); setError(null) }}
          disabled={busy}
          className="w-full py-3 px-6 rounded-2xl border-2 border-brand-text bg-white
                     text-brand-text font-semibold text-sm flex items-center justify-center gap-2
                     active:scale-95 transition-transform
                     disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {t('Create an account')}
        </button>

        <button
          onClick={() => { window.location.href = '/join' }}
          disabled={busy}
          className="w-full py-3 px-6 rounded-2xl border-2 border-brand-border bg-white
                     text-brand-text font-medium text-sm flex items-center justify-center gap-2
                     active:scale-95 transition-transform
                     disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 10h-8.35A5.99 5.99 0 007 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 005.65-4H13l2 2 2-2 2 2 2-2v-4z"
                  fill="currentColor"/>
          </svg>
          {t('Join a host with a room code')}
        </button>

        <button
          onClick={handleApple}
          disabled={busy}
          className="w-full py-3 px-6 rounded-2xl bg-black/80 text-white font-medium
                     text-sm flex items-center justify-center gap-2
                     active:scale-95 transition-transform
                     disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.29.07 2.18.74 2.94.73.95-.01 1.9-.77 3.2-.67 1.57.14 2.76.74 3.54 1.92-3.22 1.86-2.7 6.13.68 7.82-.85 2.02-1.97 3.85-2.36 4.06zM13 3.5c.13 2.32-1.93 4.25-4 4.1-.19-2.33 2.04-4.14 4-4.1z"/>
          </svg>
          {t('Sign in with Apple')}
        </button>

        {/* Google's brand guidelines require the unmodified four-colour G
            and the exact wording, on a white or dark surface. Keeping the
            official mark is the whole point, so no currentColor here. */}
        <button
          onClick={handleGoogle}
          disabled={busy}
          className="w-full py-3 px-6 rounded-2xl border-2 border-brand-border bg-white
                     text-brand-text font-medium text-sm flex items-center justify-center gap-2
                     active:scale-95 transition-transform
                     disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
            <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
            <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
            <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
          </svg>
          {t('Sign in with Google')}
        </button>

        <button onClick={handleGuest} disabled={busy} className="btn-ghost w-full">
          {t('Continue as guest')}
        </button>

        {error && <p className="text-sm text-brand-danger text-center">{error}</p>}

        <p className="text-center text-xs text-brand-muted px-4">{t('Guests can join a hosted game with a room code, read weekly stories, and see past winners. Sign in to vote, host rooms, submit to the weekly event, and unlock subscription features.')}</p>
      </div>
    </div>
  )
}

const inputClass = `mt-1 w-full px-4 py-3 rounded-xl border border-brand-border
                    bg-white text-brand-text placeholder:text-brand-muted
                    focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                    focus:ring-brand-lime/30 transition-all`

function FormShell({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  const t = useT()
  return (
    <div className="screen px-5 justify-center">
      <div className="max-w-sm mx-auto w-full space-y-6">
        <button onClick={onBack} className="btn-ghost -ml-2 mb-2">{t('← Back')}</button>
        {children}
      </div>
    </div>
  )
}

function FormHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex flex-col items-center text-center">
      <Logo size={72} className="mb-4" />
      <h1 className="font-display text-3xl font-bold text-brand-text uppercase tracking-wide">
        {title}
      </h1>
      <p className="text-brand-muted mt-1 text-sm">{subtitle}</p>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-brand-text">{label}</span>
      {children}
    </label>
  )
}
